import _ = require("lodash");
import {
  ErrorCodes,
  UploadStatus,
  Constants,
  EntityType,
  apiUriPathParameters,
  CommonConstants,
  SuccessStatusCode,
} from "../../util/UIConstants";
import { EntityConfiguration, IApiInterface } from "../../configurations/configuration";
import { labels, currentResource } from "../../util/utils";
import { UniversalService } from "../universal/UniversalService";
import { ServiceDataModel } from "../models/Models";
import {
  parseErrorData,
  translateToServiceDataModel,
  getErrorMessage,
  extractErrorDetails,
} from "../../helpers/common/DataUtilityHelper";
import { EndPoint } from "../../util";
import { getAccessToken } from "../../auth/msal";
import { ServiceUrlHelper } from "../../helpers/ServiceUrlHelper";
import { APIMiddleware } from "../../helpers/api-middleware";
import { ApiModel } from "../models/Models";
import Utility from "../../util/utilityFunctions";

export class TemplateService implements IApiInterface {
  constructor() {}

  async downloadDataAsync(
    engagementId: string,
    configuration: EntityConfiguration,
    clientRequestTypeSelectedPayload,
    ldcVersioning
  ): Promise<ServiceDataModel> {
    let dataModel: ServiceDataModel = await this.downloadApiDataAsync(
      engagementId,
      configuration,
      clientRequestTypeSelectedPayload,
      ldcVersioning
    );
    return dataModel;
  }

  async saveDataAsync(
    engagementId: string,
    modifedRequests: any,
    configuration: EntityConfiguration,
    APIBatchProcessingCounter: any,
    updatedModifiedRequest
  ): Promise<string> {
    let errorMessageText = "";
    let data = await this.saveDataBulkAsync(
      engagementId,
      modifedRequests,
      configuration,
      APIBatchProcessingCounter,
      updatedModifiedRequest
    );

    if (data?.ErrorMessage) {
      errorMessageText = data.ErrorMessage;
    } else {
      errorMessageText = data ? parseErrorData(data) : "";
    }

    if (data?.ErrorCode == ErrorCodes.ApiValidationErrorPreConditionFailed) {
      // return specfic toast error message for precondition failed
      errorMessageText = labels.ApiValidationErrorPreConditionFailedDocument;
    }
    if (data?.ErrorCode == ErrorCodes.ApiValidationErrorBadRequest) {
      let { updateErrorMessage, errors } = this.handleErrorValidation(modifedRequests, data, configuration);

      if (errors) {
        let hasNonNullIndex = errors.some((detail) => detail.index !== null && detail.errorMessage);
        if (hasNonNullIndex) {
          let isPatch = configuration.entityConfiguration["apiTemplate"]?.updateMethodType == "PATCH";
          errors.forEach((item) => {
            if (!Utility.isNullOrUndefined(item.index)) {
              let target = isPatch && updatedModifiedRequest?.length ? updatedModifiedRequest[item.index] : modifedRequests[item.index];
              if (target) {
                target.uploadStatus = UploadStatus.InvalidInput;
                target.markForUpdate = Constants.MarkForUpdateNo;
                target.uploadError = item.errorMessage;
              }
            }
          });
        }
      }

      if (updateErrorMessage) {
        errorMessageText = updateErrorMessage;
      }
    }

    return errorMessageText;
  }

  async downloadApiDataAsync(
    engagementId: string,
    configuration: EntityConfiguration,
    inputObjectParameter,
    ldcVersioning
  ): Promise<ServiceDataModel> {
    // get the valid hercules incountry uri from the universal home.json call for the engagement
    let modal: ServiceDataModel = new ServiceDataModel();
    let herculesApiBaseUriAsync: EndPoint = await UniversalService.getHerculesApiBaseUriAsync(engagementId);
    if (herculesApiBaseUriAsync.ErrorCode) {
      modal.ErrorCode = herculesApiBaseUriAsync.ErrorCode;
      return modal;
    }
    // get the client requests from the hercules incontry api call
    const herculesAccessToken: string = await getAccessToken({
      audience: herculesApiBaseUriAsync.audienceUrl,
      url: herculesApiBaseUriAsync.serviceUrl,
    });

    if (_.isEmpty(herculesAccessToken)) {
      modal.ErrorCode = ErrorCodes.AccessTokenGetError;
      return modal;
    }

    let dynamicParameters = {
      baseUrl: herculesApiBaseUriAsync.serviceUrl,
      engagementId: engagementId,
    };

    // Check if inputObjectParameter is not an empty object
    if (Object.keys(inputObjectParameter).length) {
      // Merge inputObjectParameter into dynamicParameters
      _.extend(dynamicParameters, inputObjectParameter);
    }

    let { getRequestedUrl, inputSheetGetAllCallRequired, referenceSheetAPICallRequired } = this.getMappedApiUri(
      configuration,
      dynamicParameters
    );

    const crPromises = getRequestedUrl.map((url) => APIMiddleware.fetch("GET", url, herculesAccessToken, null));
    //let response = await APIMiddleware.fetch("GET", crPromises, herculesAccessToken, null);

    let responses = await Promise.all(crPromises);

    const errorInApiCall = responses.find((response) => response.errorCode);

    let referenceFormattedData = null;
    let mainAPICallData = null;
    if (!errorInApiCall) {
      mainAPICallData = [];
      if (inputSheetGetAllCallRequired) {
        mainAPICallData = this.flattenGroupedApiData(configuration, responses[0]?.data);

        if (referenceSheetAPICallRequired) {
          //if (configuration.formatDependentAPICallData) {
          referenceFormattedData = await configuration.formatDependentAPICallData(responses, ldcVersioning, 1);
          //}
        }
      } else {
        if (referenceSheetAPICallRequired && configuration.formatDependentAPICallData) {
          referenceFormattedData = await configuration.formatDependentAPICallData(responses, ldcVersioning, 0);
        }
      }
      let dataItemsEditModel: ServiceDataModel = translateToServiceDataModel(
        mainAPICallData,
        engagementId,
        referenceFormattedData
      );
      return dataItemsEditModel;
    } else {
      let dataItemsEditModel: ServiceDataModel = translateToServiceDataModel(
        mainAPICallData,
        engagementId,
        referenceFormattedData
      );
      dataItemsEditModel.ErrorCode = errorInApiCall.errorCode;
      return dataItemsEditModel;
    }
  }

  // Some APIs return the rows grouped inside a parent object, for example time phases are grouped by milestone.
  // childDataAttributeName tells us which parent property holds the rows that belong on the input sheet.
  flattenGroupedApiData(configuration: EntityConfiguration, apiData): any {
    const childDataAttributeName = configuration.entityConfiguration["apiTemplate"]["get"]?.childDataAttributeName;
    if (!childDataAttributeName || !_.isArray(apiData)) {
      return apiData;
    }
    return _.flatMap(apiData, (groupedItem) => groupedItem?.[childDataAttributeName] ?? []);
  }

  getMappedApiUri(configuration: EntityConfiguration, parameters): any {
    let inputSheetGetAllCallRequired = false;
    let referenceSheetAPICallRequired = false;
    let requiredAPIUrl = [];
    if (configuration.entityConfiguration["apiTemplate"]["get"]?.url) {
      inputSheetGetAllCallRequired = true;
      let getApiUrl = configuration.entityConfiguration["apiTemplate"]["get"]?.url;
      let urlDynamicParameter = configuration.entityConfiguration["apiTemplate"]["get"]?.urlDynamicParameter;
      urlDynamicParameter.forEach((param) => {
        getApiUrl = getApiUrl.replace(param, parameters[param]);
      });
      requiredAPIUrl.push(getApiUrl);
    }

    if (configuration.entityConfiguration["apiTemplate"]?.dependentAPICall) {
      referenceSheetAPICallRequired = true;
      let dependentAPICall = configuration.entityConfiguration["apiTemplate"]?.dependentAPICall;
      dependentAPICall.forEach((param) => {
        let apiUrl = param.url;
        let apiUrlDynamicParameter = param.urlDynamicParameter;
        apiUrlDynamicParameter.forEach((param) => {
          apiUrl = apiUrl.replace(param, parameters[param]);
        });
        requiredAPIUrl.push(apiUrl);
      });
    }

    const apiDetails = {
      getRequestedUrl: requiredAPIUrl,
      inputSheetGetAllCallRequired: inputSheetGetAllCallRequired, // get url to fetch for entity example document, task etc
      referenceSheetAPICallRequired: referenceSheetAPICallRequired,
    };

    return apiDetails;
  }

  patchMappedApiUri(configuration: EntityConfiguration, parameters, dataToUpdate): any {
    const apitemplate = configuration.entityConfiguration["apiTemplate"];
    let apiTemplatePatch = apitemplate["patch"];
    let patchApiUrl = apiTemplatePatch?.url;
    let urlDynamicParameter = apiTemplatePatch.urlDynamicParameter;
    if (apitemplate?.bulkupdateApi === false) {
      dataToUpdate = dataToUpdate[0];
    }
    urlDynamicParameter.forEach((param) => {
      if (dataToUpdate && dataToUpdate.hasOwnProperty(param)) {
        const value = dataToUpdate[param];
        patchApiUrl = patchApiUrl.replace(param, value);
      } else {
        patchApiUrl = patchApiUrl.replace(param, parameters[param]);
      }
    });

    return patchApiUrl;
  }

  public static updatePatchInputBodyData(updatedValue: any, item: any): any {
    if (_.isArray(updatedValue)) {
      let newUpdatedValuePayload = [];
      updatedValue.forEach((valueItem) => {
        let value = {};
        value[item.payloadObjectAttributeName] = valueItem;
        newUpdatedValuePayload.push(value);
      });

      updatedValue = newUpdatedValuePayload;
    } else {
      let value = {};
      value[item.payloadObjectAttributeName] = updatedValue;
      updatedValue = value;
    }

    return updatedValue;
  }

  getPostInputBodyData(dataToUpdate: any, configuration: EntityConfiguration): any {
    let apiTemplate = configuration.entityConfiguration["apiTemplate"];
    let requestPayloadAttributes = apiTemplate.updateOutputAttributes.filter(
      (item) => item.requestPayloadAttributes != false
    );
    let defaultRequestPayload = apiTemplate.post.DefaultRequestPayload;
    let patchJsonList = [];

    dataToUpdate.map((updateItem) => {
      let payLoadObject = {};
      requestPayloadAttributes.forEach((item) => {
        const attribute = item.dataAttributeName;
        if (updateItem.hasOwnProperty(attribute) && !Utility.isInvalidValue(updateItem[attribute])) {
          let updatedValue = updateItem[attribute];
          if (item.payloadDataType == "number") {
            let updatedNumberValue = _.toNumber(updatedValue);
            if (_.isNaN(updatedNumberValue)) {
              if (item.defaultValue) {
                payLoadObject[attribute] = item.defaultValue;
              } else {
                payLoadObject[attribute] = 0;
              }
            } else {
              let valueNumber = updatedNumberValue;
              payLoadObject[attribute] = valueNumber;
            }
          } else {
            if (item.payloadObjectAttributeName && updatedValue) {
              payLoadObject[attribute] = TemplateService.updatePatchInputBodyData(updatedValue, item);
            } else {
              payLoadObject[attribute] = updatedValue;
            }
          }
        }
      });

      if (Utility.hasAnyProperty(payLoadObject)) {
        defaultRequestPayload.forEach((attribute) => {
          Object.keys(attribute).forEach((key) => {
            payLoadObject[key] = attribute[key];
          });
        });
        patchJsonList.push(payLoadObject);
      } else {
        // add error
        updateItem.uploadStatus = UploadStatus.InvalidInput;
      }
    });

    return patchJsonList;
  }

  public static getPatchInputBodyData(dataToUpdate: any, configuration: EntityConfiguration): any {
    const dataToUpdateInAPI = dataToUpdate.filter((item) => !item.isSameName);
    let apiTemplate = configuration.entityConfiguration["apiTemplate"];
    let patchTemplate = apiTemplate["patch"];
    let idValueUpdatedInpath = apiTemplate.updateOutputAttributes.filter((item) => item.idValueUpdatedInpath);
    let pathObject = idValueUpdatedInpath[0];
    let patchJsonList = [];
    let apiCallNeeded = false;
    const bodyJson = dataToUpdateInAPI.map((updateItem) => {
      let body = {};

      if (_.isArray(patchTemplate.requestBody)) {
        patchTemplate.requestBody.forEach((item) => {
          body = {};
          body["editRowIndex"] = updateItem.editRowIndex;
          let path = item.path.replace(pathObject.dataAttributeName, updateItem[pathObject?.dataAttributeName]);
          body["op"] = item.op;
          body["path"] = path;

          if (!Array.isArray(item.valueDataAttributeName)) {
            if (updateItem.hasOwnProperty(item.valueDataAttributeName)) {
              let updatedValue = updateItem[item.valueDataAttributeName];
              if (item.payloadDataType == "number") {
                let updatedNumberValue = _.toNumber(updatedValue);
                if (_.isNaN(updatedNumberValue)) {
                  if (item.defaultValue) {
                    body["value"] = item.defaultValue;
                  } else {
                    body["value"] = 0;
                  }
                } else {
                  let valueNumber = updatedNumberValue;
                  body["value"] = valueNumber;
                }
              } else {
                body["value"] = updatedValue;
              }

              if (updatedValue === undefined) {
                body["value"] = null;
              }

              if (item.payloadObjectAttributeName && updatedValue) {
                body["value"] = TemplateService.updatePatchInputBodyData(updatedValue, item);
              }

              apiCallNeeded = true;
              patchJsonList.push(body);
            }
          } else {
            let value = {};
            item.valueDataAttributeName.forEach((item) => {
              if (updateItem.hasOwnProperty(item)) {
                let updatedValue = updateItem[item];
                value[item] = updatedValue;
                if (updatedValue === undefined) {
                  value[item] = null;
                }
              }
            });
            apiCallNeeded = true;
            body["value"] = value;
            patchJsonList.push(body);
          }
        });
        if (!apiCallNeeded) {
          updateItem.uploadStatus = UploadStatus.Uploaded;
        }
        return patchJsonList;
      } else {
        let path = patchTemplate.requestBody.path.replace(
          pathObject?.dataAttributeName,
          updateItem[pathObject?.dataAttributeName]
        );
        body["editRowIndex"] = updateItem.editRowIndex;
        body["op"] = patchTemplate.requestBody.op;
        body["path"] = path;

        if (!Array.isArray(patchTemplate.requestBody.valueDataAttributeName)) {
          body["value"] = updateItem[patchTemplate.requestBody.valueDataAttributeName];
        } else {
          let value = {};
          patchTemplate.requestBody.valueDataAttributeName.forEach((item) => {
            if (updateItem.hasOwnProperty(item)) {
              value[item] = updateItem[item];
              if (value[item] === undefined) {
                value[item] = null;
              }
            }
          });
          if (_.isEmpty(value)) {
            updateItem.uploadStatus = UploadStatus.Uploaded;
          }
          body["value"] = value;
        }
      }

      return body;
    });

    // To Do , merge all items into one array bodyJson and return
    return _.isArray(patchTemplate.requestBody) ? bodyJson[0] : bodyJson;
  }

  putMappedApiUri(configuration: EntityConfiguration, parameters, data): any {
    // execute for Patch
    let dataToUpdate = data;
    let apiTemplate = configuration.entityConfiguration["apiTemplate"];
    if (apiTemplate?.bulkupdateApi === false) {
      dataToUpdate = dataToUpdate[0];
    }
    let getApiUrl = configuration.entityConfiguration["apiTemplate"]["put"].url;
    let urlDynamicParameter = configuration.entityConfiguration["apiTemplate"]["put"].urlDynamicParameter;
    urlDynamicParameter.forEach((param) => {
      if (dataToUpdate && dataToUpdate.hasOwnProperty(param)) {
        const value = dataToUpdate[param];
        getApiUrl = getApiUrl.replace(param, value);
      } else {
        getApiUrl = getApiUrl.replace(param, parameters[param]);
      }
    });

    return getApiUrl;
  }

  postMappedApiUri(configuration: EntityConfiguration, parameters, data): any {
    // execute for Patch
    let dataToUpdate = data;
    let apiTemplate = configuration.entityConfiguration["apiTemplate"];
    if (apiTemplate?.bulkupdateApi === false) {
      dataToUpdate = dataToUpdate[0];
    }
    let getApiUrl = configuration.entityConfiguration["apiTemplate"]["post"].url;
    let urlDynamicParameter = configuration.entityConfiguration["apiTemplate"]["post"].urlDynamicParameter;
    urlDynamicParameter.forEach((param) => {
      if (dataToUpdate && dataToUpdate.hasOwnProperty(param)) {
        const value = dataToUpdate[param];
        getApiUrl = getApiUrl.replace(param, value);
      } else {
        getApiUrl = getApiUrl.replace(param, parameters[param]);
      }
    });

    return getApiUrl;
  }

  getPutInputBodyData(dataToUpdate: any, configuration: EntityConfiguration): any {
    let apiTemplate = configuration.entityConfiguration["apiTemplate"];
    if (apiTemplate.bulkupdateApi === false) {
      dataToUpdate = dataToUpdate[0];
    }
    let patchTemplate = apiTemplate["put"];
    let idValueUpdatedInpath = apiTemplate.updateOutputAttributes.filter((item) => item.idValueUpdatedInpath);
    let pathObject = idValueUpdatedInpath[0];
    if (apiTemplate.bulkupdateApi === false) {
      let body = {};
      patchTemplate.requestBody.valueDataAttributeName.forEach((item) => {
        if (dataToUpdate.hasOwnProperty(item)) {
          body[item] = dataToUpdate[item];
        }
      });
      return body;
    }

    const bodyJson = dataToUpdate.map((updateItem) => {
      let body = {};
      patchTemplate.requestBody.valueDataAttributeName.forEach((item) => {
        if (updateItem.hasOwnProperty(item)) {
          body[item] = updateItem[item];
        }
      });

      return body;
    });
    return bodyJson;
  }
  async parseApiResponseDataForErrors(
    response: any,
    engagementId: string,
    dataToUpdate: Array<any>,
    configuration: EntityConfiguration
  ): Promise<ServiceDataModel> {
    let apiTemplate = configuration.entityConfiguration["apiTemplate"];
    const data = response.data;
    let modal: ServiceDataModel = new ServiceDataModel();
    if (!_.isUndefined(data)) {
      if (data && !data.errors) {
        // should execute when api response received in data returned from api
        dataToUpdate.forEach((updateItem, index) => {
          let responseItem;

          let updateMethodTemplate = apiTemplate[apiTemplate?.updateMethodType.toLowerCase()];
          const responseIdAttribute = updateMethodTemplate.responseAttributes.idAttribute;
          if (apiTemplate.updateMethodType == "POST" && responseIdAttribute) {
            const idValueUpdatedInRequestOutput = apiTemplate.updateOutputAttributes.find(
              (item) => item.idValueUpdatedInRequestOutput
            );
            if (idValueUpdatedInRequestOutput) {
              // To Do, need to check if index can be greater then records we have
              updateItem[idValueUpdatedInRequestOutput.dataAttributeName] =
                idValueUpdatedInRequestOutput?.dataType == "string"
                  ? data[index][responseIdAttribute].toString()
                  : data[index][responseIdAttribute];
            }
          }
          let idValueUpdatedInpath = apiTemplate.updateOutputAttributes.filter((item) => item.idValueUpdatedInpath);
          let pathObject = idValueUpdatedInpath[0];
          if (Array.isArray(data)) {
            // for patch when comes array as response
            responseItem = data.find(
              (item: any) => item[responseIdAttribute] == updateItem[pathObject?.dataAttributeName]
            );
            if (!responseItem) {
              if (SuccessStatusCode.includes(response.status)) {
                updateItem.uploadStatus = UploadStatus.Uploaded;
                updateItem.markForUpdate = Constants.MarkForUpdateNo;
                updateItem.uploadError = [];
              } else {
                if (responseItem?.[responseIdAttribute] == updateItem[pathObject?.dataAttributeName]) {
                  let errorMessage = data[index]?.errorMessage
                    ? data[index]?.errorMessage
                    : data[index]?.error?.message;
                  if (errorMessage) {
                    modal.ErrorCode = ErrorCodes.ApiValidationError;
                    modal.ErrorMessage = labels.errorMessageOnExcelUpdate;
                    updateItem.uploadStatus = UploadStatus.InvalidInput;
                    let message = getErrorMessage(errorMessage, configuration.configurationType);
                    updateItem.uploadError = message ? message : errorMessage;
                  }
                }
              }
            } else {
              let errorMessage = responseItem?.error?.message ? responseItem.error.message : responseItem.errorMessage;
              if (errorMessage) {
                modal.ErrorCode = ErrorCodes.ApiValidationError;
                modal.ErrorMessage = labels.errorMessageOnExcelUpdate;
                updateItem.uploadStatus = UploadStatus.InvalidInput;
                let message = getErrorMessage(errorMessage, configuration.configurationType);
                updateItem.uploadError = message ? message : errorMessage;
              } else {
                if (SuccessStatusCode.includes(response.status)) {
                  updateItem.uploadStatus = UploadStatus.Uploaded;
                  updateItem.markForUpdate = Constants.MarkForUpdateNo;
                  updateItem.uploadError = [];
                }
              }
            }
          } else {
            responseItem = data;
            if (responseItem[responseIdAttribute] == updateItem[pathObject?.dataAttributeName]) {
              updateItem.uploadStatus = UploadStatus.Uploaded;
              updateItem.markForUpdate = Constants.MarkForUpdateNo;
              updateItem.uploadError = [];
            } else {
              let errorMessage = responseItem?.error?.message ? responseItem.error.message : responseItem.errorMessage;
              if (errorMessage) {
                modal.ErrorCode = ErrorCodes.ApiValidationError;
                updateItem.uploadStatus = UploadStatus.InvalidInput;
                modal.ErrorMessage = labels.errorMessageOnExcelUpdate;
                let message = getErrorMessage(errorMessage, configuration.configurationType);
                updateItem.uploadError = message ? message : errorMessage;
              } else {
                if (SuccessStatusCode.includes(response.status)) {
                  updateItem.uploadStatus = UploadStatus.Uploaded;
                  updateItem.markForUpdate = Constants.MarkForUpdateNo;
                  updateItem.uploadError = [];
                }
              }
            }
          }
        });
      } else {
        if (!SuccessStatusCode.includes(response.status)) {
          if (response.errorCode) {
            modal.ErrorCode = response.errorCode;
          } else {
            modal.ErrorCode = ErrorCodes.ApiValidationError;
          }
        }
      }

      if (response.errorCode) {
        modal.ErrorCode = response.errorCode;
      }

      modal.response = response;
      modal.apiResponseReturned = response.apiResponseReturned;
      return modal;
    } else {
      modal.apiResponseReturned = response.apiResponseReturned;
      modal.ErrorCode = ErrorCodes.HerculesApiNoResponseError;
      modal.response = response;
    }
    return modal;
  }

  /**
   * - uses service to fetch the in-country engagement lds url, if engagement url is not available in AppConfigInstance.ad.endPoints then get universal token, call univseral api to get engagement ldc information
   * - get hercules ldc token based on information from universal, call hercules api to save data
   * - logs the error in the properties
   * @param engagementId engagementId to update data
   * @param dataToUpdate list of records to PATCH
   * @returns void
   */
  async saveDataBulkAsync(
    engagementId: string,
    dataToUpdate: Array<any>,
    configuration: any,
    APIBatchProcessingCounter: any,
    updatedModifiedRequest: any
  ): Promise<ServiceDataModel> {
    let modal: ServiceDataModel = new ServiceDataModel();
    if (_.isEmpty(dataToUpdate)) {
      return;
    }

    // get the valid hercules incountry uri from the universal home.json call for the engagement
    let herculesApiBaseUriAsync: EndPoint = await UniversalService.getHerculesApiBaseUriAsync(engagementId);

    if (herculesApiBaseUriAsync.ErrorCode) {
      modal.ErrorCode = herculesApiBaseUriAsync.ErrorCode;
      return modal;
    }

    // check if the engagement ldc version is same as the ldc version while populating.
    // When user populates engagement in old LDC and then LDC get updated in between then we have show user an error message
    if (configuration.entityConfiguration.apiTemplate?.versionUpdateCheckRequired) {
      if (
        parseFloat(herculesApiBaseUriAsync.datacenterversion) !=
        parseFloat(configuration?.settings?.ldcVersionWhenEngagementLoads)
      ) {
        modal.ErrorMessage = labels.engagementLDCVersionUpdateError;
        return modal;
      }
    }

    const herculesAccessToken: string = await getAccessToken({
      audience: herculesApiBaseUriAsync.audienceUrl,
      url: herculesApiBaseUriAsync.serviceUrl,
    });

    if (_.isEmpty(herculesAccessToken)) {
      modal.ErrorCode = ErrorCodes.AccessTokenGetError;
      return modal;
    }
    let dynamicParameters = {
      baseUrl: herculesApiBaseUriAsync.serviceUrl,
      engagementId: engagementId,
    };
    let apiTemplate = configuration.entityConfiguration["apiTemplate"];
    let apiUrl = "";
    let bodyJson: any = {};
    if (apiTemplate?.updateMethodType == "PATCH") {
      // execute for Patch
      apiUrl = this.patchMappedApiUri(configuration, dynamicParameters, dataToUpdate);
      bodyJson = dataToUpdate.map(({ editRowIndex, ...rest }) => ({ ...rest }));
      //This main dataToUpdate is used to update excel formatting based on API records and in case if patch operation this need to be updated with modified request that has been used for that particular batch API call. This will change for every batch
      //dataToUpdate hold batch data that is used for API call
      //updatedModifiedRequest holds the modified request for that particluare batch API call
      dataToUpdate = updatedModifiedRequest;
      //bodyJson = this.getPatchInputBodyData(dataToUpdate, configuration);
    }

    if (apiTemplate?.updateMethodType == "POST") {
      // execute for Patch
      apiUrl = this.postMappedApiUri(configuration, dynamicParameters, dataToUpdate);
      bodyJson = this.getPostInputBodyData(dataToUpdate, configuration);
    }

    if (apiTemplate?.updateMethodType == "PUT") {
      apiUrl = this.putMappedApiUri(configuration, dynamicParameters, dataToUpdate);
      bodyJson = this.getPutInputBodyData(dataToUpdate, configuration);
    }

    if (_.isEmpty(bodyJson)) {
      return;
    }

    let jsonObjectForNonEmptyValues = [];

    _.forEach(bodyJson, (item: any) => {
      if (Utility.isNullOrUndefined(item.value)) {
        jsonObjectForNonEmptyValues.push(item);
      } else {
        // will work when value has multiple properties structure instead of single value
        // ToDo, need to check item.value contains list of properties
        // if (Object.keys(item.value).length > 0) {
        jsonObjectForNonEmptyValues.push(item);
        // }
      }
    });

    // If the new array is empty, return early
    if (jsonObjectForNonEmptyValues.length) {
      // Update the bodyJson with the new array
      bodyJson = jsonObjectForNonEmptyValues;
    } else {
      return;
    }

    //change undefined payload to empty Json because strigify remove undefined from payload but parameter should go to api to validate data at api side
    // _.forEach(bodyJson, (item: any) => {
    //   if (item.value) {
    //     // Iterate through the keys of the value object
    //     _.forEach(item.value, (value, key) => {
    //       if (value === undefined) {
    //         item.value[key] = null;
    //       }
    //     });
    //   }
    // });

    let response = await APIMiddleware.fetch(
      apiTemplate?.updateMethodType,
      apiUrl,
      herculesAccessToken,
      JSON.stringify(bodyJson)
    );

    APIBatchProcessingCounter.current = APIBatchProcessingCounter.current + 1;

    return await this.parseApiResponseDataForErrors(response, engagementId, dataToUpdate, configuration);
  }

  handleErrorValidation = (
    modifedRequests: Array<any>,
    data: ServiceDataModel,
    configuration: EntityConfiguration
  ): any => {
    let updateErrorMessage = "";
    // find the individuals records if possible if we can then show below message in toast
    let dataErrors = data.response?.data?.errors ?? data.response?.data;

    let errors = extractErrorDetails(dataErrors);
    if (errors.some((detail) => detail.errorMessage)) {
      updateErrorMessage = labels.errorMessageOnExcelUpdate;
      return { updateErrorMessage, errors };
    }

    // ToDo, will remove with Task, Document Template Integration and Release_2.13 with api changes
    let errorArray = data.response?.data?.errors;
    updateErrorMessage = labels.errorMessageOnExcelUpdate;
    //Patch.Operations[0].value
    let errorMessageList = [];
    const allKeys = Object.keys(errorArray);
    // Use this object to keep track of changed props
    // Iterate through keys
    allKeys.forEach((key) => {
      let itemID = null;
      let errorID = null;
      let requestErrorMessage = null;
      let value = errorArray[key];
      let path = value[0].split("/");
      if (path.length >= 2) {
        itemID = path[1];
      }
      if (itemID) {
        let errorData = value[0].split("Error:");
        let apiErrorMessage = "";
        if (errorData?.length >= 2) {
          apiErrorMessage = errorData[1];
          let errorValue = apiErrorMessage?.split("-");
          errorID = errorValue[0]?.trim();
          if (!isNaN(Number(errorID))) {
            requestErrorMessage = currentResource.errors[errorID];
          }
          if (requestErrorMessage === undefined) {
            requestErrorMessage = errorValue[1];
          }
          errorMessageList.push({ itemID: itemID, errorMessage: requestErrorMessage });
        }
        let dataErrorArray = errorMessageList;
        let apiTemplate = configuration.entityConfiguration["apiTemplate"];
        let idValueUpdatedInpath = apiTemplate.updateOutputAttributes.filter((item) => item.idValueUpdatedInpath);
        let pathObject = idValueUpdatedInpath[0];
        dataErrorArray.forEach((error) => {
          let index = modifedRequests.findIndex((request) => request[pathObject?.dataAttributeName] === error.itemID);
          if (index >= 0) {
            modifedRequests[index] = {
              ...modifedRequests[index],
              uploadError: error.errorMessage,
              uploadStatus: UploadStatus.InvalidInput,
            };
            updateErrorMessage = labels.errorMessageOnExcelUpdate;
          }
        });
      } else {
        let errorData = errorArray[key];
        let errorValue = errorData[0]?.split("-");
        errorID = errorValue[0]?.trim();
        if (!isNaN(Number(errorID))) {
          requestErrorMessage = currentResource.errors[errorID];
        }
        if (requestErrorMessage === undefined) {
          requestErrorMessage = errorValue[1];
        }

        modifedRequests.forEach((item) => {
          item["uploadError"] = requestErrorMessage;
          item["uploadStatus"] = UploadStatus.InvalidInput;
          updateErrorMessage = labels.errorMessageOnExcelUpdate;
        });
      }
    });

    return { updateErrorMessage, errors };
  };

  public createOrDeleteTags = async (
    dataToUpdate: any,
    engagementId: string,
    herculesApiBaseUriAsync: EndPoint,
    herculesAccessToken: string,
    errorToastMessage: string,
    renderUIAfterPostProcessingSteps: () => void,
    callBackOnSaveToServer: (error) => void,
    configuration: EntityConfiguration
  ) => {
    const recordsToUpdateEngagementTagIds = _.filter(dataToUpdate, (item) => {
      return (
        item.uploadStatus == UploadStatus.Uploaded &&
        !_.isEqual(_.sortBy(item?.EngagementTagIdsOriginalIds), _.sortBy(item?.EngagementTagIds))
      );
    });

    if (recordsToUpdateEngagementTagIds.length > 0) {
      errorToastMessage = await this.updatingTags(
        recordsToUpdateEngagementTagIds,
        engagementId,
        herculesApiBaseUriAsync.serviceUrl,
        herculesAccessToken,
        errorToastMessage,
        renderUIAfterPostProcessingSteps,
        callBackOnSaveToServer,
        configuration
      );
    } else {
      renderUIAfterPostProcessingSteps && await renderUIAfterPostProcessingSteps();
    }
  };

  public static calculateDataAttributes = (
    data: any,
    otherSourceData: any,
    configuration: EntityConfiguration
  ): any => {
    let templateGetApi = configuration?.entityConfiguration[CommonConstants.ApiTemplate]?.get;
    if (!templateGetApi) {
      return;
    }

    let apiResponseData = templateGetApi.dataAttributeName
      ? data.response[templateGetApi.dataAttributeName]
      : data.response;

    templateGetApi.calculateDisplayDataatribute.forEach((calculateAttribute) => {
      apiResponseData.forEach((apiData) => {
        let lookupData = otherSourceData[calculateAttribute.lookupDataAttributeList];

        if (calculateAttribute.calculateAttributeType === CommonConstants.Join) {
          let dataSourceList = apiData[calculateAttribute.dataAttributeListName];
          let calculatedField =
            dataSourceList &&
            dataSourceList
              .map((item) => item[calculateAttribute.apiDataAttributeId])
              .join(calculateAttribute.separator);

          apiData[calculateAttribute.displayAttributeName] = calculatedField;
          return;
        }

        if (calculateAttribute.calculateAttributeType === CommonConstants.FilterWithJoin) {
          let dataSourceList = apiData[calculateAttribute.dataAttributeListName];
          let mappedIds = dataSourceList && dataSourceList.map((item) => item[calculateAttribute.apiDataAttributeId]);

          // Updated below code to not display "; " after the the last item
          let joineddata = "";
          mappedIds &&
            mappedIds.forEach((item, index) => {
              calculateAttribute.lookupDataAttribute.forEach((lookupDataAttribute) => {
                let valueCompared = lookupDataAttribute.apiDataAttributeId
                  ? apiData[lookupDataAttribute.apiDataAttributeId]
                  : item;
                let dataFound = lookupData.find(
                  (data) => data[lookupDataAttribute.lookupDataAttribute] == valueCompared
                );

                if (dataFound) {
                  joineddata += dataFound[calculateAttribute.displayAttributeName];
                  if (index !== mappedIds.length - 1) {
                    joineddata += calculateAttribute.separator;
                  }
                }
              });
            });

          apiData[calculateAttribute.displayAttributeName] = joineddata;
          return;
        }

        let filteredList =
          lookupData &&
          lookupData.filter((data) =>
            calculateAttribute.lookupDataAttribute.every(
              (lookupDataAttribute) =>
                data[lookupDataAttribute.lookupDataAttribute] === apiData[lookupDataAttribute.apiDataAttributeId]
            )
          );

        if (filteredList.length > 0) {
          let requestData = filteredList[0];
          apiData[calculateAttribute.displayAttributeName] = requestData[calculateAttribute.displayAttributeName];
        }

        // Remove timestamp from dueDate property
        if (apiData.dueDate) {
          apiData.dueDate = apiData.dueDate?.split("T")[0];
        }
      });
    });
  };

  async postProcessingSteps(
    dataToUpdate: any,
    configuration: EntityConfiguration,
    engagementId: string,
    renderUIAfterPostProcessingSteps,
    callBackOnSaveToServer: (error) => void,
    errorMessage
  ) {
    let modal: ServiceDataModel = new ServiceDataModel();
    let errorToastMessage = "";
    if (_.isEmpty(dataToUpdate)) {
      return;
    }
    if (!configuration.entityConfiguration["apiTemplate"]?.["additionalAPICallRequired"]) {
      return renderUIAfterPostProcessingSteps && renderUIAfterPostProcessingSteps();
    }
    // get the valid hercules incountry uri from the universal home.json call for the engagement
    let herculesApiBaseUriAsync: EndPoint = await UniversalService.getHerculesApiBaseUriAsync(engagementId);
    if (herculesApiBaseUriAsync.ErrorCode) {
      modal.ErrorCode = herculesApiBaseUriAsync.ErrorCode;
      const errorMessageText = parseErrorData(modal);
      callBackOnSaveToServer(errorMessageText);
      return;
    }
    const herculesAccessToken: string = await getAccessToken({
      audience: herculesApiBaseUriAsync.audienceUrl,
      url: herculesApiBaseUriAsync.serviceUrl,
    });

    if (_.isEmpty(herculesAccessToken)) {
      modal.ErrorCode = ErrorCodes.AccessTokenGetError;
      const errorMessageText = parseErrorData(modal);
      callBackOnSaveToServer(errorMessageText);
      return;
    }

    if (configuration.entityConfiguration["apiTemplate"]?.["additionalAPICallRequired"]?.tagsAPICall) {
      await this.createOrDeleteTags(
        dataToUpdate,
        engagementId,
        herculesApiBaseUriAsync,
        herculesAccessToken,
        errorToastMessage,
        renderUIAfterPostProcessingSteps,
        callBackOnSaveToServer,
        configuration
      );
    } else {
      renderUIAfterPostProcessingSteps && renderUIAfterPostProcessingSteps();
    }

    return errorToastMessage;
  }

  batchTagsBasedOnEntities = (apiModalListPost, batchSize = 500) => {
    const batchedList = [];

    apiModalListPost.forEach((apiModel) => {
      const { payload } = apiModel;
      const { entitiesUIds, ...restPayload } = payload;

      if (entitiesUIds.length > batchSize) {
        for (let i = 0; i < entitiesUIds.length; i += batchSize) {
          const batchedEntities = entitiesUIds.slice(i, i + batchSize);
          batchedList.push({ ...apiModel, payload: { ...restPayload, entitiesUIds: batchedEntities } });
        }
      } else {
        batchedList.push(apiModel);
      }
    });

    // Group the batchedList into arrays of batchSize
    const groupedBatches = [];
    for (let i = 0; i < batchedList.length; i += batchSize) {
      groupedBatches.push(batchedList.slice(i, i + batchSize));
    }

    return groupedBatches;
  };

  getTagsErrorMessage = (configuration) => {
    switch (configuration.configurationType) {
      case EntityType.AddClientRequest:
        return labels.addclinetRequestTagError;
      case EntityType.EditClientRequest:
        return labels.editclinetRequestTagError;
      case EntityType.AddOstTask:
        return labels.invalidDataInTags;
      case EntityType.Task:
        return labels.editTaskAPIError;
    }
  };

  // Utility function to split an array into chunks
  createBatch<T>(array: T[], batchsize = 500): T[][] {
    const batchSize = batchsize;
    const batchResult: T[][] = [];
    for (let i = 0; i < array.length; i += batchSize) {
      batchResult.push(array.slice(i, i + batchSize));
    }
    return batchResult;
  }

  groupTag(dataToUpdate: any, attributeName: string): any {
    let tagsData = {};
    dataToUpdate.forEach((item) => {
      if (_.isArray(item[attributeName])) {
        item[attributeName].forEach((tagId) => {
          if (!tagsData[tagId]) {
            tagsData[tagId] = [];
          }
          tagsData[tagId].push(item);
        });
      }
    });

    return tagsData;
  }

  async updatingTags(
    dataToUpdate,
    engagementId: string,
    herculesApiBaseUriAsync,
    herculesAccessToken,
    errorToastMessage,
    renderUIAfterPostProcessingSteps,
    callBackOnSaveToServer,
    configuration
  ) {
    const postProcessingCall =
      configuration.entityConfiguration["apiTemplate"]?.["additionalAPICallRequired"]?.tagsAPICall;
    let tagsGroup = _.groupBy(dataToUpdate, "EngagementTagIds");
    const gropupedTagData = this.groupTag(dataToUpdate, "EngagementTagIds");
    Object.keys(gropupedTagData)?.length > 0 ? (tagsGroup = gropupedTagData) : tagsGroup;

    const OriginalGropupedTagData = this.groupTag(dataToUpdate, "EngagementTagIdsOriginalIds");

    let allTags = _.uniq([...Object.keys(tagsGroup), ...Object.keys(OriginalGropupedTagData)]);
    let apiModalListDelete: ApiModel[] = [];
    let apiModalListPost: ApiModel[] = [];
    allTags.forEach(async (tagId) => {
      const itemTagId = tagId?.toString();
      let items = tagsGroup[itemTagId];
      let IdFortagsGroup = [];
      if (items) {
        IdFortagsGroup = items.map((Request) => Request[postProcessingCall?.payloadObject]);
      }
      const getPostUrl = (): string =>
        ServiceUrlHelper.addClientRequestEngagementTagsApiEndPointUrlFromBaseUrl(
          herculesApiBaseUriAsync,
          engagementId,
          itemTagId
        );
      let originalTagIdItem = OriginalGropupedTagData[itemTagId];
      if (originalTagIdItem) {
        const IdForOriginalTagsGroup = originalTagIdItem.map((Request) => Request?.[postProcessingCall?.payloadObject]);
        let deletedIds = _.difference(IdForOriginalTagsGroup, IdFortagsGroup);
        if (deletedIds.length > 0) {
          let apiModel = new ApiModel();
          apiModel.Method = "DELETE";
          apiModel.id = itemTagId;
          let payload = {
            engagementId: engagementId,
            entitiesUIds: deletedIds,
            tagId: itemTagId,
            entityId: postProcessingCall?.entityId, // 17 is the entity id for client request
          };
          apiModel.payload = payload;
          apiModel.url = getPostUrl();
          apiModalListDelete.push(apiModel);

          console.log("deletedIds", deletedIds);
        }
        let matchingIds = _.intersection(IdFortagsGroup, IdForOriginalTagsGroup);
        let newIds = _.difference(IdFortagsGroup, matchingIds);

        if (newIds.length > 0) {
          let apiModel = new ApiModel();
          apiModel.Method = "POST";
          apiModel.id = itemTagId;
          let payload = {
            engagementId: engagementId,
            entitiesUIds: newIds,
            tagId: itemTagId,
            entityId: postProcessingCall?.entityId, // 17 is the entity id for client request
          };
          apiModel.payload = payload;
          apiModel.url = getPostUrl();
          apiModalListPost.push(apiModel);
        }
        console.log("newIds", newIds);
      } else {
        let apiModel = new ApiModel();
        apiModel.Method = "POST";
        apiModel.id = itemTagId;
        let payload = {
          engagementId: engagementId,
          entitiesUIds: IdFortagsGroup,
          tagId: itemTagId,
          entityId: postProcessingCall?.entityId, // 17 is the entity id for client request
        };
        apiModel.payload = payload;
        apiModel.url = getPostUrl();
        apiModalListPost.push(apiModel);
      }
    });

    let batchesPost = this.batchTagsBasedOnEntities(apiModalListPost, 500);
    for (let i = 0; i < batchesPost.length; i++) {
      const batch = batchesPost[i];
      if (batch[0]?.payload?.tagId) {
        const promises = batch.map(async (apiModel) => {
          try {
            let response = await APIMiddleware.fetch(
              apiModel.Method,
              apiModel.url,
              herculesAccessToken,
              JSON.stringify(apiModel.payload)
            );
            const data = response?.data;
            if (data) {
              if (!response.ok && response.status !== 200) {
                errorToastMessage = labels.APIValidationError;
                let apiErrorItemsRecord = dataToUpdate.filter((item) => {
                  let dataToCompare = apiModel.payload["entitiesUIds"];
                  return dataToCompare?.includes(item?.[postProcessingCall?.payloadObject]);
                });
                apiErrorItemsRecord.forEach((item) => {
                  item.uploadStatus = UploadStatus.PostProcessingStepsError;
                  item.uploadError = this.getTagsErrorMessage(configuration);
                });
              }
            }
          } catch (error) {
            console.error(error);
          }
        });

        await Promise.all(promises);
      }
    }

    let batchesDelete = this.batchTagsBasedOnEntities(apiModalListDelete, 500);
    for (let i = 0; i < batchesDelete.length; i++) {
      const batch = batchesDelete[i];

      const promises = batch.map(async (apiModel) => {
        try {
          let response = await APIMiddleware.fetch(
            apiModel.Method,
            apiModel.url,
            herculesAccessToken,
            JSON.stringify(apiModel.payload)
          );
          const data = response?.data;
          if (response.status !== 204) {
            errorToastMessage = labels.APIValidationError;
            let apiErrorItemsRecord = dataToUpdate.filter((item) => {
              let dataToCompare = apiModel.payload["entitiesUIds"];
              return dataToCompare?.includes(item?.[postProcessingCall?.payloadObject]);
            });
            apiErrorItemsRecord.forEach((item) => {
              item.uploadStatus = UploadStatus.PostProcessingStepsError;
              item.uploadError = this.getTagsErrorMessage(configuration);
            });
          }
        } catch (error) {
          console.error(error);
        }
      });

      await Promise.all(promises);
    }

    renderUIAfterPostProcessingSteps && await renderUIAfterPostProcessingSteps();
    callBackOnSaveToServer(errorToastMessage);

    return errorToastMessage;
  }
}
